import { SearchError, SearchResult } from '@ecs-services/search/search.interface';
import { createReducer, on } from '@ngrx/store';
import { OrderNumberSearchResult } from '../../features/result/spare-parts-result/components/order-number-table/order-number-table.interface';
import { productPageReducers } from './result-specific/product-page/product-page.reducer';
import { sparePartReducers } from './result-specific/spare-part/spare-part.reducer';
import * as SearchActions from './search.actions';

export const searchFeatureKey = 'search';

export enum DataStage {
    /**
     * No data yet, needs initialization
     */
    UNINITIALIZED = 0,

    /**
     * Received data that needs further processing/requests
     */
    INTERMEDIATE = 20,

    /**
     * Received enough data for display
     */
    SUFFICIENT = 30,

    /**
     * Received everything we can, there won't be additional data to fetch
     */
    FINISHED = 40,
}

export interface SearchState<T extends SearchResult = SearchResult> {
    /**
     * "State" of the current state. Whether it's complete or needs (more) data.
     */
    dataStage: DataStage;
    /**
     * Search term of currently active search
     */
    searchTerm: string[];
    electronicsTabSearchTerm: string[];
    /**
     * Response of latest search.
     * `null` if an error occurred
     */
    searchResult: T;
    /**
     * Error response of the latest search
     * `null` if no error occured
     */
    searchError: SearchError | null | undefined;
    electronicsTabSearchError: SearchError | null | undefined;
    /**
     * Response of previous search.
     * `null` if an error occurred
     */
    previousSearchResult: T;
    /**
     * Previously SUCCESSFUL search terms (does include the active search)
     */
    searchHistory: string[];

    orderNumberSearchResult: OrderNumberSearchResult | null | undefined;
}

export const initialState: SearchState = {
    dataStage: DataStage.UNINITIALIZED,
    searchTerm: [],
    electronicsTabSearchTerm: [],
    searchResult: undefined,
    searchError: undefined,
    electronicsTabSearchError: undefined,
    previousSearchResult: undefined,
    searchHistory: [],
    orderNumberSearchResult: undefined,
};

export const reducer = createReducer(
    initialState,

    on(SearchActions.requestSearchResult, (state, { payload: { searchTerm } }) => ({
        ...state,
        searchTerm,
        // overwrite previous `searchResult`, especially to clear the `errors` property
        orderNumberSearchResult: initialState.orderNumberSearchResult,
        searchResult: initialState.searchResult,
        searchError: initialState.searchError,
    })),

    on(SearchActions.requestSearchResultElectronics, (state, { payload: { searchTerm } }) => ({
        ...state,
        electronicsTabSearchTerm: searchTerm,
        // overwrite previous `searchResult`, especially to clear the `errors` property
        orderNumberSearchResult: initialState.orderNumberSearchResult,
        searchResult: initialState.searchResult,
        electronicsTabSearchError: initialState.electronicsTabSearchError,
    })),

    on(SearchActions.receiveSearchResult, (state, { payload: { searchResult, searchError } }) => ({
        ...state,
        searchResult:
            hasErrorsProperty(state.searchResult) && hasErrorsProperty(searchResult)
                ? {
                      ...searchResult,
                      errors: [...new Set([...state.searchResult.errors, ...searchResult.errors])],
                  }
                : hasErrorsProperty(searchResult)
                  ? {
                        ...searchResult,
                        errors: [...new Set([...searchResult.errors])],
                    }
                  : searchResult,
        searchError,
        electronicsTabSearchError: initialState.electronicsTabSearchError,
        orderNumberSearchResult: state.orderNumberSearchResult,
        previousSearchResult: state.searchResult,
    })),

    on(SearchActions.receiveElectronicsError, (state, { payload: { electronicsTabSearchError } }) => ({
        ...state,
        electronicsTabSearchError: electronicsTabSearchError,
        orderNumberSearchResult: state.orderNumberSearchResult,
        previousSearchResult: state.searchResult,
    })),

    on(SearchActions.receiveOrderNumberSearchResult, (state, { payload: { orderNumberSearchResult } }) => ({
        ...state,
        orderNumberSearchResult,
    })),

    on(SearchActions.updateOrderNumberItemChecked, (state, { payload: { orderNumberItemsChecked } }) => ({
        ...state,
        searchError: state.searchError ? { ...state.searchError, orderNumberItemsChecked } : null,
        orderNumberSearchResult: null,
    })),

    on(SearchActions.updateSearchHistory, (state, { payload: { searchHistory } }) => ({
        ...state,
        searchHistory: searchHistory.length ? [...new Set(searchHistory.concat(state.searchHistory))] : [],
        previousSearchResult: state.searchResult,
    })),

    on(SearchActions.updateStage, (state, { dataStage }) => ({
        ...state,
        dataStage,
    })),

    on(SearchActions.updateSearchError, (state, { payload: { searchError } }) => ({
        ...state,
        searchError,
    })),

    ...productPageReducers,
    ...sparePartReducers
);

const hasErrorsProperty = (result: SearchResult): result is SearchResult & { errors: string[] } =>
    !!result && 'errors' in result && Array.isArray(result.errors);
